import {define, inject, singleton} from '@injex/core';
import {AlertsManager} from "../../../common/alerts/managers/alertsManager.mdl";
import ReportingMetaDataManager from "../../../common/managers/ReportingMetaDataManager.mdl";
import {IBiReportingParams} from "../common/interfaces/IReportingParams";
import URLQueryParams from "../../../common/utils/URLQueryParams";
import IReportingFilter from "../common/interfaces/IReportingFilter";
import IReportingConstraint from "../common/interfaces/IReportingConstraint";
import IReportingEntry from "../common/interfaces/IReportingEntry";
import {ReportVerticalType} from "../../../common/enums/ReportVerticalType";
import {AlertAccent} from "../../../common/alerts/common/enums";

const toUnix = (date: any): number | undefined => {
    if (date === null || date === undefined) return undefined;
    if (typeof date === "number") return date;
    return date.isValid?.() ? date.unix() : undefined;
};

@define()
@singleton()
export class GenerateReportUrlParamsManager {

    @inject() private alertsManager: AlertsManager;
    @inject() private reportingMetaDataManager: ReportingMetaDataManager;

    /**
     * Builds the query string for a shareable report link from the current editor state.
     * `prepared` supplies filter values by ID (more precise than labels); everything else is
     * read from `params`, so the link works before a report is generated.
     */
    public generate(prepared: {filters: Partial<IReportingFilter>[]}, params: IBiReportingParams) {
        try {
            const fields = params.fields || [];
            const formulas = fields.filter((field) => field.type === "formula");

            const raw: any = {
                t: params.time,
                tz: params.timezone,
                from: toUnix(params.from),
                to: toUnix(params.to),
                v: params.verticalType,
                fs: fields.map(this.fieldToken),
                gs: (params.groups || []).map((group) => group.value),
                f: this.flattenFilters(prepared.filters || [], params.verticalType),
                c: this.flattenConstraints(params.constraints || [], params.verticalType),
            };

            if (formulas.length) {
                raw.fm = formulas.map((field) => ({label: field.label, expression: field.formulaExpression || ""}));
            }

            const subGroups = (params.subGroups || []).filter((subGroup) => subGroup.group);
            if (subGroups.length) {
                raw.sg = subGroups.map(({group, entities, targeting, freeField}) => ({group, entities, targeting, freeField}));
            }

            if (params.dbType) {
                raw.db = params.dbType;
            }

            if (params.isCompare) {
                raw.compare = true;
                raw.p = params.period;
                raw.cfrom = toUnix(params.compareFrom);
                raw.cto = toUnix(params.compareTo);
            }

            return URLQueryParams.stringify(raw);
        } catch (e) {
            this.alertsManager.setBasicAlert({content: "Failed to generate report URL params"}, AlertAccent.Error);
            return "";
        }
    }

    // Formulas are restored by label (with their expression in `fm`), capsules by ID, other metrics by key.
    private fieldToken = (field: IReportingEntry): string => {
        if (field.type === "formula") return field.label;
        if (field.type === "capsule") return (field as any)._id;
        return field.value;
    };

    public flattenFilters(filters: Partial<IReportingFilter>[], vertical: ReportVerticalType) {

        return filters.map((filter) => {
            if (filter.key && filter.values && filter.values.length) {
                const group = this.reportingMetaDataManager.getFilterByValue(filter.key, vertical);

                if (group) {
                    return {
                        key: group.label,
                        exclude: filter.exclude,
                        operator: filter.operator,
                        values: filter.values.map((value) => value[filter.filterLabelKey] || value)
                    };
                }
            }
            return null;
        }).filter(Boolean);
    }

    public flattenConstraints(constraints: IReportingConstraint[], vertical: ReportVerticalType) {

        return constraints.map((constraint) => {
            if (constraint.name && constraint.op && constraint.value) {
                const field = this.reportingMetaDataManager.getFieldByValue(constraint.name, vertical);

                if (field) {
                    const {value, label} = field;
                    return {
                        name: {label, value},
                        op: constraint.op,
                        value: constraint.value
                    };
                }
            }
            return null;
        }).filter(Boolean);
    }

}
